import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as SelectPrimitive from '@radix-ui/react-select';

import { Select, SelectContent, SelectItem } from 'src/component/ds/ui/select';
import {
  SegmentedToggle, segmentedToggleItemVariants, type SegmentedToggleOption
} from 'src/component/ds/ui/segmentedToggle';
import { cn } from 'src/component/ds/utils';
import { fetchUnits, setSelectedUnit } from 'src/module/filter/action';
import { getClientId } from 'src/module/authentication/selector';
import { getSelectedUnit, getUnitNames } from 'src/module/filter/selector';
import useBreakpoint from 'src/hooks/useBreakpoint';
import { useTranslation } from 'src/i18n';

const MENU_CLASSES = 'ds-max-h-[320px]';

/** The app-wide "no filter" sentinel. */
const NO_AREA = '';

/*
 * How many areas the row holds before the rest go to the menu, per breakpoint.
 */
const AREA_COUNT = { phone: 1, mobile: 5, desktop: 5 };

/*
 * "Area" is this screen's name for a unit: the data is devices.unit and the
 * selection rides in the shared filter slice, so this is the same filter the
 * old UI's toggle bar sets. Areas that don't fit go behind a "+N more" menu.
 */
export default function AreaFilter ({ className }: { className?: string }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const clientId = useSelector(getClientId);
  const areas = useSelector(getUnitNames);
  const selectedUnit = useSelector(getSelectedUnit);

  useEffect(() => {
    dispatch(fetchUnits());
  }, [dispatch, clientId]);

  /* The filter slice is persisted, so the stored area can outlive the facility
   * having it -- fall back to All Areas rather than showing nothing selected. */
  const selected = areas.includes(selectedUnit) ? selectedUnit : NO_AREA;

  const { isMobile, isPhone } = useBreakpoint();
  const limit = isPhone ? AREA_COUNT.phone : isMobile ? AREA_COUNT.mobile : AREA_COUNT.desktop;

  /* The row is the first `limit` areas, plus the selection when it's past them
   * so it stays visible; the menu is everything past `limit` either way. */
  const { inline, overflow } = useMemo(() => {
    const head = areas.slice(0, limit);
    const rest = areas.slice(limit);
    const appended = selected && !head.includes(selected) ? [selected] : [];

    return { inline: [...head, ...appended], overflow: rest };
  }, [areas, selected, limit]);

  const options: SegmentedToggleOption[] = useMemo(
    () => [
      { value: NO_AREA, label: t('dashboard.allAreas') },
      ...inline.map((area) => ({ value: area, label: area })),
    ],
    [inline, t]
  );

  const setArea = (area: string) => dispatch(setSelectedUnit(area));

  return (
    <div className={cn('ds-flex ds-items-center', className)} data-testid='area-filter'>
      <SegmentedToggle
        options={options}
        value={selected}
        onChange={setArea}
        aria-label={t('dashboard.areaFilterLabel')}
        // The trigger butts onto the row, so the row drops its right pill end.
        className={cn(overflow.length && 'segmented-toggle--open-end')}
        testId='area-filter-segment'
      />

      {overflow.length
        ? (
          <Select value={selected} onValueChange={setArea}>
            <SelectPrimitive.Trigger
              className={cn(
                segmentedToggleItemVariants({ left: 'inner', right: 'end' }),
                'data-[state=open]:ds-bg-muted'
              )}
              aria-label={t('dashboard.areaFilterMoreLabel', { count: overflow.length })}
              data-testid='area-filter-more'
            >
              {t('dashboard.areaFilterMore', { count: overflow.length })}
            </SelectPrimitive.Trigger>
            <SelectContent align='start' className={MENU_CLASSES}>
              {overflow.map((area) => (
                <SelectItem key={area} value={area} data-testid={`area-filter-option-${area}`}>
                  {area}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )
        : null}
    </div>
  );
}
